import axios from "axios";

export type FeedKind = "observation" | "anomaly" | "data_quality" | "beach_ranking" | string;
export type FeedConfidence = "high" | "medium" | "low" | "unknown";
export type FeedbackAction =
  | "useful"
  | "not_useful"
  | "needs_more_context"
  | "open_details"
  | "open_source"
  | "expert_confirmed";

export interface FeedContextRef {
  context_id: string;
  label: string;
}

export interface FeedCandidate {
  candidate_id: string;
  report_id: string;
  kind: FeedKind;
  title: string;
  summary: string;
  contexts: FeedContextRef[];
  confidence: FeedConfidence;
  published_at: string;
  priority: number;
  source_refs: string[];
  facts: Record<string, unknown>;
}
export interface PublicEnvironmentalPublication {
  release_id: string;
  impression_id: string;
  published_at: string;
  created_at: string;
  candidate: FeedCandidate;
}

export interface FeedItem {
  candidate: FeedCandidate;
  position: number;
  propensity: number;
}

export interface FeedResponse {
  impression_id: string;
  request: Record<string, unknown>;
  response: {
    impression_id: string;
    request_id: string;
    items: FeedItem[];
    policy_id: string;
    policy_mode: string;
    feature_schema_version: string;
    feature_schema_digest: string;
    feature_vector: Record<string, number>;
  };
  created_at: string;
  feedback: Array<{
    feedback_id: string;
    candidate_id: string;
    action: FeedbackAction;
    reason: string;
    occurred_at: string;
  }>;
}

export interface FeedHistoryResponse {
  items: FeedResponse[];
  next_before: string | null;
  has_more: boolean;
}

export interface FeedFeedbackResponse {
  accepted: boolean;
  feedback_id: string;
  impression_id: string;
  candidate_id: string;
  action: FeedbackAction;
  reason: string;
  occurred_at: string;
}

const apiHost = process.env.REACT_APP_API_HOST ?? "";
const feedApi = axios.create({
  baseURL: process.env.REACT_APP_FEED_API_HOST ?? `${apiHost}/api/v1`,
  timeout: 30000,
});

export async function getLatestEnvironmentalFeed(): Promise<FeedResponse> {
  const response = await feedApi.get<FeedResponse>("/environmental/feed/latest");
  return response.data;
}

export async function getEnvironmentalFeedHistory(
  before?: string,
  limit = 10
): Promise<FeedHistoryResponse> {
  const response = await feedApi.get<FeedHistoryResponse>(
    "/environmental/feed/history",
    { params: { limit, ...(before ? { before } : {}) } }
  );
  return response.data;
}

export async function getPublicEnvironmentalPublication(
  candidateId: string
): Promise<PublicEnvironmentalPublication> {
  const response = await feedApi.get<PublicEnvironmentalPublication>(
    `/environmental/feed/publications/${encodeURIComponent(candidateId)}`
  );
  return response.data;
}

export function getEnvironmentalPublicationShareUrl(candidateId: string): string {
  const path = `/api/v1/share/environmental-feed/${encodeURIComponent(candidateId)}`;
  if (process.env.NODE_ENV === "production" && typeof window !== "undefined") {
    return `${window.location.origin}${path}`;
  }

  const configuredBase = process.env.REACT_APP_PUBLIC_SHARE_BASE_URL;
  const apiBase = process.env.REACT_APP_API_HOST ?? "";
  const base = configuredBase || `${apiBase}/api/v1/share`;
  return `${base.replace(/\/$/, "")}/environmental-feed/${encodeURIComponent(candidateId)}`;
}

export async function sendEnvironmentalFeedFeedback(
  impressionId: string,
  candidateId: string,
  action: FeedbackAction,
  reason = ""
): Promise<FeedFeedbackResponse> {
  const response = await feedApi.post<FeedFeedbackResponse>(
    "/environmental/feed/feedback",
    {
      impression_id: impressionId,
      candidate_id: candidateId,
      action,
      reason,
    }
  );
  return response.data;
}
